import React, { useState } from 'react';
import {
  Typography, Card, Row, Col, Button, Table, Tag, Space, Modal, Form,
  Input, Select, Alert, Tabs, Statistic, Progress, Tooltip, message,
  Popconfirm, Switch, InputNumber, Badge, Descriptions
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  WarningOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  ThunderboltOutlined, ApiOutlined, CopyOutlined, InfoCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useAllRPCs, useCreateRPC, useUpdateRPC, useDeleteRPC,
  useBulkDeleteRPCs, useTestRPC, useBulkTestRPCs
} from '../hooks/useRPCDashboard';
import { useSupportedNetworks } from '../hooks/useWalletManagement';
import type { RPCEndpoint } from '../types/rpc';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const RPCManagementPageNew: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('1');
  const [selectedNetwork, setSelectedNetwork] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [showAllActiveNetworks, setShowAllActiveNetworks] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRPC, setEditingRPC] = useState<RPCEndpoint | null>(null);
  const [testingRPCId, setTestingRPCId] = useState<string | null>(null);
  const [form] = Form.useForm();

  // Fetch all networks for filter and form
  const { data: networksData, isLoading: isLoadingNetworks } = useSupportedNetworks();

  // Get all networks from the networks API
  // Create a map of display name (title) to actual network value (full_name) for filtering
  const networkMap = React.useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (!networksData?.data) return map;

    networksData.data.forEach((network: any) => {
      const title = network.title || network.name;
      const fullName = network.full_name || title;
      map.set(title, fullName);
    });

    return map;
  }, [networksData]);

  // Convert selected network title to full_name for API query
  const networkFilterValue = selectedNetwork ? networkMap.get(selectedNetwork) : undefined;

  // Fetch RPCs with filters
  const { data: rpcsData, isLoading, refetch } = useAllRPCs({
    network: networkFilterValue,
    status: selectedStatus,
    activeNetworksOnly: showAllActiveNetworks,
    page,
    limit: pageSize
  });

  // Mutations
  const createRPC = useCreateRPC();
  const updateRPC = useUpdateRPC();
  const deleteRPC = useDeleteRPC();
  const bulkDeleteRPCs = useBulkDeleteRPCs();
  const testRPC = useTestRPC();
  const bulkTestRPCs = useBulkTestRPCs();

  // Get network display names for the dropdown
  const networks = React.useMemo<string[]>(() => {
    return Array.from(networkMap.keys()).sort();
  }, [networkMap]);

  // Calculate RPC count per network for quick filter badges
  const networkRPCCounts = React.useMemo<Map<string, { total: number; active: number }>>(() => {
    const counts = new Map<string, { total: number; active: number }>();
    if (!rpcsData?.data) return counts;

    rpcsData.data.forEach((rpc: RPCEndpoint) => {
      // Find the display name for this network
      let displayName = rpc.network;
      networkMap.forEach((fullName, title) => {
        if (fullName === rpc.network) {
          displayName = title;
        }
      });

      const current = counts.get(displayName) || { total: 0, active: 0 };
      current.total++;
      if (rpc.status === 'active') {
        current.active++;
      }
      counts.set(displayName, current);
    });

    return counts;
  }, [rpcsData, networkMap]);

  // Handle create/edit modal
  const showModal = (rpc?: RPCEndpoint) => {
    if (rpc) {
      setEditingRPC(rpc);

      // Convert network full_name back to title for display in form
      let networkTitle = rpc.network;
      networkMap.forEach((fullName, title) => {
        if (fullName === rpc.network) {
          networkTitle = title;
        }
      });

      form.setFieldsValue({
        ...rpc,
        network: networkTitle
      });
    } else {
      setEditingRPC(null);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Convert network title to full_name before sending to API
      const networkFullName = networkMap.get(values.network) || values.network;
      const dataToSend = {
        ...values,
        network: networkFullName
      };

      if (editingRPC) {
        await updateRPC.mutateAsync({ id: editingRPC._id, data: dataToSend });
        message.success('RPC endpoint updated successfully');
      } else {
        await createRPC.mutateAsync(dataToSend);
        message.success('RPC endpoint created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingRPC(null);
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingRPC(null);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      await deleteRPC.mutateAsync(id);
      message.success('RPC endpoint deleted successfully');
    } catch (error: any) {
      message.error(error.message || 'Delete failed');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      await bulkDeleteRPCs.mutateAsync(selectedRowKeys as string[]);
      message.success(`Deleted ${selectedRowKeys.length} RPC endpoints`);
      setSelectedRowKeys([]);
    } catch (error: any) {
      message.error(error.message || 'Bulk delete failed');
    }
  };

  // Handle bulk test
  const handleBulkTest = async () => {
    try {
      const result = await bulkTestRPCs.mutateAsync(selectedRowKeys as string[]);
      message.success(
        `Tested ${result.total} RPCs: ${result.successCount} healthy, ${result.failureCount} unhealthy`
      );
      setSelectedRowKeys([]);
    } catch (error: any) {
      message.error(error.message || 'Bulk test failed');
    }
  };

  // Handle test RPC
  const handleTestRPC = async (rpcId: string, url: string, network: string) => {
    try {
      setTestingRPCId(rpcId);
      const result = await testRPC.mutateAsync({ url, network });
      console.log('Test RPC result:', result);

      // Check if result has isHealthy property
      if (result && result.isHealthy === true) {
        message.success('RPC endpoint is healthy - Status updated');
        // Refetch data immediately to show updated status
        await refetch();
      } else if (result && result.isHealthy === false) {
        message.warning('RPC endpoint is not responding - Status updated');
        // Refetch data immediately to show updated status
        await refetch();
      } else {
        // Fallback: check if we got a valid response
        message.warning('Unable to determine RPC health status');
      }
    } catch (error: any) {
      message.error(error.message || 'Test failed');
    } finally {
      setTestingRPCId(null);
    }
  };

  // Status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'success', icon: <CheckCircleOutlined />, text: 'Active' },
      inactive: { color: 'error', icon: <CloseCircleOutlined />, text: 'Inactive' },
      unknown: { color: 'default', icon: <WarningOutlined />, text: 'Unknown' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unknown;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // Priority badge
  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      critical: { color: 'red', text: 'Critical' },
      high: { color: 'orange', text: 'High' },
      normal: { color: 'blue', text: 'Normal' },
      low: { color: 'default', text: 'Low' }
    };
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Table columns
  const columns: ColumnsType<RPCEndpoint> = [
    {
      title: 'Network',
      dataIndex: 'network',
      key: 'network',
      sorter: (a, b) => a.network.localeCompare(b.network),
      render: (network: string) => <Text strong>{network}</Text>
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      width: 300,
      render: (url: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={url}>
            <Text
              ellipsis
              style={{
                flex: 1,
                minWidth: 0
              }}
            >
              {url}
            </Text>
          </Tooltip>
          <Tooltip title="Copy URL">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(url);
                message.success('URL copied to clipboard');
              }}
              style={{
                padding: '0 4px',
                minWidth: '20px',
                height: '20px',
                flexShrink: 0
              }}
            />
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => <Tag>{type.toUpperCase()}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Unknown', value: 'unknown' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: string, record: RPCEndpoint) => (
        <Space direction="vertical" size={0}>
          {getStatusBadge(status)}
          {(status === 'inactive' || status === 'unknown') && record.error && (
            <Tooltip title={record.error}>
              <Tag
                icon={<ExclamationCircleOutlined />}
                color="error"
                style={{ cursor: 'pointer', fontSize: '11px', marginTop: '4px' }}
              >
                Error Details
              </Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Priority',
      dataIndex: 'priority_level',
      key: 'priority_level',
      width: 100,
      filters: [
        { text: 'Critical', value: 'critical' },
        { text: 'High', value: 'high' },
        { text: 'Normal', value: 'normal' },
        { text: 'Low', value: 'low' }
      ],
      onFilter: (value, record) => record.priority_level === value,
      render: (priority: string) => getPriorityBadge(priority)
    },
    {
      title: 'Success Rate',
      dataIndex: 'success_rate',
      key: 'success_rate',
      width: 120,
      sorter: (a, b) => a.success_rate - b.success_rate,
      render: (rate: number) => (
        <Progress
          percent={Math.round(rate)}
          size="small"
          status={rate >= 80 ? 'success' : rate >= 50 ? 'normal' : 'exception'}
        />
      )
    },
    {
      title: 'Avg Response',
      dataIndex: 'avg_response_time',
      key: 'avg_response_time',
      width: 120,
      sorter: (a, b) => a.avg_response_time - b.avg_response_time,
      render: (time: number) => (
        <Text type={time > 5000 ? 'danger' : time > 2000 ? 'warning' : 'success'}>
          {Math.round(time)}ms
        </Text>
      )
    },
    {
      title: 'Checks',
      key: 'checks',
      width: 100,
      render: (_, record) => (
        <Tooltip title={`Success: ${record.success_count} / Failure: ${record.failure_count}`}>
          <Text>{record.check_count}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Auto Recovery',
      dataIndex: 'auto_recovery_enabled',
      key: 'auto_recovery_enabled',
      width: 120,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Test RPC">
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={() => handleTestRPC(record._id, record.url, record.network)}
              loading={testingRPCId === record._id}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this RPC endpoint?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteRPC.isPending}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Calculate statistics from global stats (for all active networks)
  const stats = React.useMemo(() => {
    if (!rpcsData?.globalStats) {
      return {
        totalNetworks: 0,
        total: 0,
        active: 0,
        inactive: 0,
        unknown: 0
      };
    }
    return {
      totalNetworks: rpcsData.globalStats.totalNetworks,
      total: rpcsData.globalStats.totalRPCs,
      active: rpcsData.globalStats.activeRPCs,
      inactive: rpcsData.globalStats.inactiveRPCs,
      unknown: rpcsData.globalStats.unknownRPCs
    };
  }, [rpcsData]);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Title level={2}>
            <ApiOutlined /> RPC Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Add RPC
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title="Active Networks"
              value={stats.totalNetworks}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="Total RPCs"
              value={stats.total}
              prefix={<ApiOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="Active"
              value={stats.active}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="Inactive"
              value={stats.inactive}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="Unknown"
              value={stats.unknown}
              valueStyle={{ color: '#faad14' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Network Filter - Active Networks Only */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#1890ff' }} />
            <Text strong>Quick Filter - Active Networks</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              (Click to filter by network)
            </Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Space size={[8, 8]} wrap>
          <Badge
            count={stats.total}
            showZero
            color="#1890ff"
            offset={[5, 0]}
            style={{ zIndex: 1 } as React.CSSProperties}
          >
            <Button
              type={!selectedNetwork && !showAllActiveNetworks ? 'primary' : 'default'}
              onClick={() => {
                setSelectedNetwork(undefined);
                setShowAllActiveNetworks(false);
              }}
              icon={<ApiOutlined />}
              size="large"
            >
              All Networks
            </Button>
          </Badge>
          <Badge
            count={rpcsData?.globalStats?.totalRPCs || 0}
            showZero
            color="#52c41a"
            offset={[5, 0]}
            style={{ zIndex: 1 } as React.CSSProperties}
          >
            <Tooltip title={`RPCs from ${rpcsData?.globalStats?.totalNetworks || 0} active networks`}>
              <Button
                type={showAllActiveNetworks ? 'primary' : 'default'}
                onClick={() => {
                  setShowAllActiveNetworks(!showAllActiveNetworks);
                  setSelectedNetwork(undefined);
                }}
                icon={<CheckCircleOutlined />}
                size="large"
                style={{
                  borderColor: showAllActiveNetworks ? '#52c41a' : '#d9d9d9',
                  fontWeight: showAllActiveNetworks ? 'bold' : 'normal'
                }}
              >
                All Active Networks
              </Button>
            </Tooltip>
          </Badge>
          {networksData?.data
            ?.filter((network: any) => network.status === 'Active')
            .sort((a: any, b: any) => {
              const aTitle = a.title || a.name;
              const bTitle = b.title || b.name;
              return aTitle.localeCompare(bTitle);
            })
            .map((network: any) => {
              const title = network.title || network.name;
              const isSelected = selectedNetwork === title && !showAllActiveNetworks;
              const counts = networkRPCCounts.get(title) || { total: 0, active: 0 };
              const hasRPCs = counts.total > 0;

              return (
                <Badge
                  key={network.full_name}
                  count={counts.total}
                  showZero={false}
                  color={counts.active > 0 ? '#52c41a' : '#ff4d4f'}
                  offset={[5, 0]}
                  style={{ zIndex: 1 } as React.CSSProperties}
                >
                  <Tooltip
                    title={
                      hasRPCs
                        ? `${counts.active} active / ${counts.total} total RPCs`
                        : 'No RPCs configured'
                    }
                  >
                    <Button
                      type={isSelected ? 'primary' : 'default'}
                      onClick={() => {
                        setShowAllActiveNetworks(false);
                        setSelectedNetwork(isSelected ? undefined : title);
                      }}
                      size="large"
                      style={{
                        borderColor: isSelected ? '#1890ff' : '#d9d9d9',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        opacity: hasRPCs ? 1 : 0.6
                      }}
                    >
                      {title}
                    </Button>
                  </Tooltip>
                </Badge>
              );
            })}
        </Space>
      </Card>

      {/* Advanced Filters */}
      <Card
        title={<Text strong>Advanced Filters</Text>}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Select
              placeholder="Filter by Network"
              allowClear
              style={{ width: '100%' }}
              value={selectedNetwork}
              onChange={setSelectedNetwork}
              showSearch
              filterOption={(input, option) => {
                if (!option || !option.children) return false;
                const label = String(option.children);
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {networks.map((network: string) => (
                <Option key={network} value={network}>{network}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by Status"
              allowClear
              style={{ width: '100%' }}
              value={selectedStatus}
              onChange={setSelectedStatus}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="unknown">Unknown</Option>
            </Select>
          </Col>
          <Col span={8}>
            {selectedRowKeys.length > 0 && (
              <Space>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={handleBulkTest}
                  loading={bulkTestRPCs.isPending}
                >
                  Test Selected ({selectedRowKeys.length})
                </Button>
                <Popconfirm
                  title={`Delete ${selectedRowKeys.length} selected RPC endpoints?`}
                  onConfirm={handleBulkDelete}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={rpcsData?.data || []}
          rowKey="_id"
          loading={isLoading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          expandable={{
            expandedRowRender: (record: RPCEndpoint) => (
              <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Descriptions
                      title={
                        <Space>
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          <Text strong>Detailed Information</Text>
                        </Space>
                      }
                      bordered
                      size="small"
                      column={2}
                    >
                      <Descriptions.Item label="Network">{record.network}</Descriptions.Item>
                      <Descriptions.Item label="Type">{record.type.toUpperCase()}</Descriptions.Item>
                      <Descriptions.Item label="API Method">{record.api_method}</Descriptions.Item>
                      <Descriptions.Item label="Request Type">{record.api_method_request_type}</Descriptions.Item>
                      <Descriptions.Item label="Priority Level">{getPriorityBadge(record.priority_level)}</Descriptions.Item>
                      <Descriptions.Item label="Priority Score">{record.priority_score.toFixed(2)}</Descriptions.Item>
                      <Descriptions.Item label="Total Checks">{record.check_count}</Descriptions.Item>
                      <Descriptions.Item label="Success Count">
                        <Text type="success">{record.success_count}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Failure Count">
                        <Text type="danger">{record.failure_count}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Success Rate">
                        <Progress
                          percent={Math.round(record.success_rate)}
                          size="small"
                          status={record.success_rate >= 80 ? 'success' : record.success_rate >= 50 ? 'normal' : 'exception'}
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="Avg Response Time">
                        <Text type={record.avg_response_time > 5000 ? 'danger' : record.avg_response_time > 2000 ? 'warning' : 'success'}>
                          {Math.round(record.avg_response_time)}ms
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Checked">
                        {record.last_checked ? new Date(record.last_checked).toLocaleString() : 'Never'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Alerts Enabled">
                        <Tag color={record.alerts_enabled ? 'green' : 'default'}>
                          {record.alerts_enabled ? 'Yes' : 'No'}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Auto Recovery">
                        <Tag color={record.auto_recovery_enabled ? 'green' : 'default'}>
                          {record.auto_recovery_enabled ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </Descriptions.Item>
                      {record.recovery_attempts !== undefined && (
                        <>
                          <Descriptions.Item label="Recovery Attempts">{record.recovery_attempts}</Descriptions.Item>
                          <Descriptions.Item label="Recovery Success">
                            <Text type="success">{record.recovery_success || 0}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Recovery Failure">
                            <Text type="danger">{record.recovery_failure || 0}</Text>
                          </Descriptions.Item>
                        </>
                      )}
                      {record.alert_threshold_response_time && (
                        <Descriptions.Item label="Alert Threshold (Response Time)">
                          {record.alert_threshold_response_time}ms
                        </Descriptions.Item>
                      )}
                      {record.alert_threshold_success_rate && (
                        <Descriptions.Item label="Alert Threshold (Success Rate)">
                          {record.alert_threshold_success_rate}%
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Full URL" span={2}>
                        <Space>
                          <Text code copyable>{record.url}</Text>
                        </Space>
                      </Descriptions.Item>
                      {record.error && (
                        <Descriptions.Item label="Error Details" span={2}>
                          <Alert
                            message="Last Error"
                            description={
                              <Text code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {record.error}
                              </Text>
                            }
                            type="error"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                          />
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Col>
                </Row>
              </div>
            ),
            rowExpandable: (record: RPCEndpoint) => true,
            expandIcon: ({ expanded, onExpand, record }) => (
              <Tooltip title={expanded ? 'Hide Details' : 'Show Details'}>
                <Button
                  type="text"
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={(e) => onExpand(record, e)}
                  style={{
                    color: (record.status === 'inactive' || record.status === 'unknown') && record.error ? '#ff4d4f' : '#1890ff'
                  }}
                />
              </Tooltip>
            )
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: rpcsData?.pagination?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize || 20);
            }
          }}
          scroll={{ x: 1500 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingRPC ? 'Edit RPC Endpoint' : 'Create RPC Endpoint'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        confirmLoading={createRPC.isPending || updateRPC.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: 'http',
            api_method_request_type: 'POST',
            priority_level: 'normal',
            alert_threshold_response_time: 5000,
            alert_threshold_success_rate: 80,
            alerts_enabled: true,
            auto_recovery_enabled: true
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="network"
                label="Network"
                rules={[{ required: true, message: 'Please select network' }]}
              >
                <Select
                  placeholder="Select network"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  loading={isLoadingNetworks}
                >
                  {networks.map((network: string) => (
                    <Option key={network} value={network}>
                      {network}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="http">HTTP</Option>
                  <Option value="socket">Socket</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="url"
            label="RPC URL"
            rules={[
              { required: true, message: 'Please enter RPC URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="api_method"
                label="API Method"
                rules={[{ required: true, message: 'Please enter API method' }]}
              >
                <Input placeholder="e.g., eth_blockNumber, getHealth" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="api_method_request_type"
                label="Request Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="priority_level"
            label="Priority Level"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="critical">Critical</Option>
              <Option value="high">High</Option>
              <Option value="normal">Normal</Option>
              <Option value="low">Low</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="alert_threshold_response_time"
                label="Alert Threshold (ms)"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="alert_threshold_success_rate"
                label="Success Rate Threshold (%)"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="alerts_enabled"
                label="Alerts Enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="auto_recovery_enabled"
                label="Auto Recovery"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default RPCManagementPageNew;

